/* import __COLOCATED_TEMPLATE__ from './question-generation-banner.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { ResponseError } from 'embercom/lib/inbox/requests';
import {
  type PlaygroundQuestionGenerationJobWireFormat,
  QuestionGenerationStatus,
} from 'embercom/lib/fin-playground/question-generation';
import type PlaygroundApiService from 'embercom/services/playground-api';

interface QuestionGenerationBannerArgs {
  status?: QuestionGenerationStatus;
  requiresUserAcknowledgement?: boolean;
  groupId?: number;
  isRunningForApp: boolean;
  onResetLatestJobData: () => void;
  onUpdateLatestJobData: (json: PlaygroundQuestionGenerationJobWireFormat) => void;
}

type QuestionGenerationBannerSignature = {
  Args: QuestionGenerationBannerArgs;
};

export default class QuestionGenerationBanner extends Component<QuestionGenerationBannerSignature> {
  @service declare appService: $TSFixMe;
  @service declare intl: $TSFixMe;
  @service declare notificationsService: $TSFixMe;
  @service declare playgroundApi: PlaygroundApiService;

  get shouldShowBanner(): boolean {
    return (
      this.isRunning || this.needsFailureAcknowledgement || this.needsEmptyResultAcknowledgement
    );
  }

  get isRunning(): boolean {
    return this.args.status === QuestionGenerationStatus.Running;
  }

  get needsEmptyResultAcknowledgement(): boolean {
    return (
      this.args.status === QuestionGenerationStatus.EmptyResult &&
      this.args.requiresUserAcknowledgement === true
    );
  }

  get needsFailureAcknowledgement(): boolean {
    return (
      this.args.status === QuestionGenerationStatus.Failed &&
      this.args.requiresUserAcknowledgement === true
    );
  }

  @action
  cancelQuestionGeneration() {
    taskFor(this._cancelQuestionGenerationTask).perform(this.args.groupId);
  }

  @action
  hideQuestionGenerationBanner() {
    taskFor(this._acknowledgeQuestionGenerationJob).perform(this.args.groupId);
  }

  @dropTask
  *_cancelQuestionGenerationTask(groupId?: number) {
    try {
      yield this.playgroundApi.cancelQuestionGeneration(groupId);

      this.args.onResetLatestJobData();
    } catch (error) {
      // If there is no job to cancel we will receive 409
      if (error instanceof ResponseError && error.response.status === 409) {
        this.args.onResetLatestJobData();
      } else if (error instanceof ResponseError && error.response.status === 404) {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.banners.group-deleted-error'),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.banners.generating-questions.cancel-error'),
        );
      }
    }
  }

  @dropTask
  *_acknowledgeQuestionGenerationJob(groupId?: number) {
    try {
      let response = (yield this.playgroundApi.acknowledgeQuestionGeneration(groupId)) as Response;

      let json = (yield response.json()) as PlaygroundQuestionGenerationJobWireFormat;
      this.args.onUpdateLatestJobData(json);
    } catch (error) {
      // If there is no job to acknowledge we will receive 409
      if (error instanceof ResponseError && error.response.status === 409) {
        this.args.onResetLatestJobData();
      } else if (error instanceof ResponseError && error.response.status === 404) {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.banners.group-deleted-error'),
        );
      } else {
        this.notificationsService.notifyError(
          this.intl.t('ai-agent.playground.banners.generating-questions.acknowledge-error'),
        );
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Playground::QuestionGenerationBanner': typeof QuestionGenerationBanner;
  }
}
